// The Pulse HTTP client (packages/shared/src/http/pulse.ts; Slice 6; D27; ADR-0023).
// Every response parses through the SAME shared Zod schema apps/api validates against
// (app-engineering §2 "parse, don't validate" - the Slice-2 wire-drift lesson), matching
// src/api/money.ts's/certify.ts's own established convention: inline the literal path
// string (`PULSE_ROUTES.pulse`'s own template, http/pulse.ts:12-14), never hand-roll a
// wire shape a shared schema already defines.
//
// LANDED (`PULSE_ROUTES`, http/pulse.ts:12-14; `PulseController`,
// apps/api/src/pulse/pulse.controller.ts:29-48): ONE route, `GET /v1/apps/:appId/pulse`
// -> `PulseView` (the seven founder metrics, D27 wall, no cost surface).
//
// NO READ-BACK ROUTE FOR `AnomalySignal[]`/`ChurnSignalKind` EXISTS (verified this pass,
// not invented): `PulseController` registers exactly one `@Get()` handler
// (pulse.controller.ts:37-47); `PulseView` (pulse.ts:57-61) is `z.strictObject({appId,
// metrics})` and carries no anomaly/churn field at any depth. Server-side,
// `pulse.service.ts:111-130,162-165` DOES detect churn signals and DOES route a fired
// signal into `intake_items` on every read - but that write is an internal ops side
// effect of this same GET, never read back to the client on ANY endpoint this slice
// lands. UX §2.2's own "Pulse reads ... `AnomalySignal`/`ChurnSignalKind` ... `intake_
// items` (to confirm a signal was in fact routed)" and UX §4.4's "Churn signals present/
// empty" state rows both assume a wire source that is not on disk.
//
// This file deliberately adds NO client call for it - a call against a route that
// provably 404s (no such path is registered anywhere in `PulseController`) would be
// worse than omitting it, and risks this screen mis-reading "never checked" as "checked,
// nothing found," the exact confusion UX §4.4 warns against ("a founder should be able
// to tell 'checked, nothing found' from 'never checked'"). Flagged as an open item for
// the tech-lead/backend-engineer - unlike src/api/money.ts's own PROPOSED-endpoint
// convention (`changeCeiling`, built against the UX spec's own explicitly proposed verb
// + path), the UX spec never proposes a literal shape for this one (its state table's
// own "API touchpoint" column reads "—" for both churn-signal rows), so nothing is
// invented here even as a PROPOSED shape.
//
// `../screens/pulse/PulseTab.tsx` renders the Churn-signals section from an always-empty
// `AnomalySignal[]` for this exact reason; `PulseAnomalyCallout`/`PulseChurnSignalCard`
// (apps/web/src/components/) are built ready-to-wire and directly unit-tested, but never
// invoked with live data by this screen until that read-back route lands.
import { PulseView } from "@nightcrew/shared";
import type { AppId, Result } from "@nightcrew/shared";
import { apiFetch } from "./client.js";
import type { ClientError } from "./errors.js";

/** GET /v1/apps/:appId/pulse (http/pulse.ts:12-14,22-24) -> the seven founder metrics. */
export const getPulse = (appId: AppId): Promise<Result<PulseView, ClientError>> =>
  apiFetch(`/v1/apps/${appId}/pulse`, PulseView);
